import { useSelector } from 'react-redux';
import { getLdapId } from '../Utils/msalUtils';

/**
 * Hook that determines the current user's access level for a given team.
 *
 * Teams with no registry entry (e.g. loaded from the backend API) are treated
 * as fully open — any user can manage rules and process batches for them.
 *
 * Teams created through the TeamManager UI have an owner (`createdBy`) and
 * an optional `members` list.  Access rules:
 *  - createdBy → isAdmin = true, hasAccess = true
 *  - member    → isAdmin = false, hasAccess = true
 *  - anyone else → isAdmin = false, hasAccess = false
 *
 * @param {string} teamValue  The team's value identifier (or '' for "All Teams")
 * @returns {{ isAdmin: boolean, hasAccess: boolean, isManaged: boolean,
 *             createdBy: string|null, members: string[] }}
 */
const useTeamAccess = (teamValue) => {
  const teamRegistry = useSelector((state) => state.dataCategorization.teamRegistry);
  const currentUser = getLdapId();

  if (!teamValue) {
    // No team selected — nothing to restrict
    return { isAdmin: false, hasAccess: true, isManaged: false, createdBy: null, members: [] };
  }

  const reg = teamRegistry?.[teamValue];
  if (!reg) {
    // No registry entry = backend/legacy team, open to all
    return { isAdmin: false, hasAccess: true, isManaged: false, createdBy: null, members: [] };
  }

  const isAdmin = reg.createdBy === currentUser;
  const hasAccess = isAdmin || (reg.members || []).includes(currentUser);

  return {
    isAdmin,
    hasAccess,
    isManaged: true,
    createdBy: reg.createdBy || null,
    members: reg.members || [],
  };
};

export default useTeamAccess;
